/**
 * Hook for managing a single travel trip with all its items and events.
 */

import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { travelApi } from '../api';
import { queryKeys } from '@/lib/query-keys';
import type {
  TravelTripResponse,
  TravelItemResponse,
  TravelEventResponse,
  TravelAttachmentResponse,
} from '../api';
import { useTravelFeature } from './useTravelUserProfile';

export function useTravelTrip(tripId: string | undefined) {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.travel.detail(tripId ?? '');
  const mapFeature = useTravelFeature('map');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () => travelApi.getTravelTripDetail(tripId!),
    enabled: !!tripId,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: queryKeys.travel.my() });
  };

  // Debounce timers keyed by itemId so rapid keystrokes collapse to a single
  // geocode call per row. 2s matches the task's spec and is well above
  // Nominatim's 1 req/sec floor.
  const geocodeTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const mapEnabledRef = useRef(mapFeature.enabled);
  mapEnabledRef.current = mapFeature.enabled;

  useEffect(() => {
    const timers = geocodeTimersRef.current;
    return () => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
    };
  }, []);

  /**
   * Schedule a geocode 2s from now, cancelling any previous timer for the same
   * row. Skipped entirely when the map feature is disabled.
   */
  const scheduleAutoGeocode = (itemId: string, query: string) => {
    if (!mapEnabledRef.current) return;
    const trimmed = query.trim();
    if (!trimmed) return;
    const existing = geocodeTimersRef.current.get(itemId);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      geocodeTimersRef.current.delete(itemId);
      if (!mapEnabledRef.current) return;
      travelApi.geocodeTravelItem(itemId, trimmed).then(
        () => invalidate(),
        () => {
          // Geocode failures are non-fatal — the pin simply won't appear.
          // A future iteration could surface a toast here.
        },
      );
    }, 2000);
    geocodeTimersRef.current.set(itemId, timer);
  };

  const updateTripMut = useMutation({
    mutationFn: (input: { title?: string; startDate?: string; status?: string }) =>
      travelApi.updateTravelTrip(tripId!, input),
    onSuccess: invalidate,
  });

  const addDayMut = useMutation({
    mutationFn: (input: Record<string, unknown>) => travelApi.createTravelDay(tripId!, input),
    onSuccess: invalidate,
  });

  const addTransitMut = useMutation({
    mutationFn: (input: Record<string, unknown>) => travelApi.createTravelTransit(tripId!, input),
    onSuccess: invalidate,
  });

  const updateItemMut = useMutation({
    mutationFn: ({ itemId, input }: { itemId: string; input: Record<string, unknown> }) =>
      travelApi.updateTravelItem(itemId, input),
    onSuccess: invalidate,
  });

  const deleteItemMut = useMutation({
    mutationFn: (itemId: string) => travelApi.deleteTravelItem(itemId),
    onSuccess: invalidate,
  });

  const reorderItemsMut = useMutation({
    mutationFn: (itemIds: string[]) => travelApi.reorderTravelItems(tripId!, itemIds),
    onSuccess: invalidate,
  });

  const addEventMut = useMutation({
    mutationFn: ({ itemId, input }: { itemId: string; input: Record<string, unknown> }) =>
      travelApi.createTravelEvent(itemId, input),
    onSuccess: invalidate,
  });

  const updateEventMut = useMutation({
    mutationFn: ({ eventId, input }: { eventId: string; input: Record<string, unknown> }) =>
      travelApi.updateTravelEvent(eventId, input),
    onSuccess: invalidate,
  });

  const deleteEventMut = useMutation({
    mutationFn: (eventId: string) => travelApi.deleteTravelEvent(eventId),
    onSuccess: invalidate,
  });

  const importTripMut = useMutation({
    mutationFn: (importData: Record<string, unknown>) =>
      travelApi.importTravelTrip(tripId!, importData),
    onSuccess: invalidate,
  });

  const duplicateDayMut = useMutation({
    mutationFn: (itemId: string) => travelApi.duplicateTravelDay(itemId),
    onSuccess: invalidate,
  });

  const reorderEventsMut = useMutation({
    mutationFn: ({ itemId, eventIds }: { itemId: string; eventIds: string[] }) =>
      travelApi.reorderTravelEvents(itemId, eventIds),
    onSuccess: invalidate,
  });

  const moveEventMut = useMutation({
    mutationFn: ({ eventId, targetItemId }: { eventId: string; targetItemId: string }) =>
      travelApi.moveTravelEvent(eventId, targetItemId),
    onSuccess: invalidate,
  });

  const duplicateEventMut = useMutation({
    mutationFn: ({ eventId, targetItemId }: { eventId: string; targetItemId: string }) =>
      travelApi.duplicateTravelEvent(eventId, targetItemId),
    onSuccess: invalidate,
  });

  const addItemAttachmentMut = useMutation({
    mutationFn: ({ itemId, fileId, label }: { itemId: string; fileId: string; label?: string }) =>
      travelApi.addTravelItemAttachment(itemId, fileId, label),
    onSuccess: invalidate,
  });

  const addEventAttachmentMut = useMutation({
    mutationFn: ({ eventId, fileId, label }: { eventId: string; fileId: string; label?: string }) =>
      travelApi.addTravelEventAttachment(eventId, fileId, label),
    onSuccess: invalidate,
  });

  const removeAttachmentMut = useMutation({
    mutationFn: ({ attachmentId, documentId }: { attachmentId: string; documentId: string }) =>
      travelApi.removeTravelAttachment(attachmentId, documentId),
    onSuccess: invalidate,
  });

  return {
    trip: data?.trip ?? (null as TravelTripResponse | null),
    items: data?.items ?? ([] as TravelItemResponse[]),
    events: data?.events ?? ([] as TravelEventResponse[]),
    attachments: data?.attachments ?? ([] as TravelAttachmentResponse[]),
    loading: isLoading,
    error: error instanceof Error ? error.message : null,
    refresh: refetch,
    // Trip mutations
    updateTrip: (input: { title?: string; startDate?: string; status?: string }) =>
      updateTripMut.mutateAsync(input),
    // Item mutations
    addDay: (input?: Record<string, unknown>) => addDayMut.mutateAsync(input ?? {}),
    addTransit: (input?: Record<string, unknown>) => addTransitMut.mutateAsync(input ?? {}),
    updateItem: async (itemId: string, input: Record<string, unknown>) => {
      const result = await updateItemMut.mutateAsync({ itemId, input });
      // Auto-geocode when city/country/dayTitle changes and the row has no
      // coordinates yet. The user can always override by calling the geocode
      // endpoint manually from the day editor.
      const touchesLocation = 'city' in input || 'country' in input || 'dayTitle' in input;
      const next = result.item;
      if (touchesLocation && next.lat == null && next.lng == null) {
        const parts = [next.city, next.country].filter(Boolean);
        const query = parts.length > 0 ? parts.join(', ') : (next.dayTitle ?? '');
        scheduleAutoGeocode(itemId, String(query ?? ''));
      }
      return result;
    },
    deleteItem: (itemId: string) => deleteItemMut.mutateAsync(itemId),
    reorderItems: (itemIds: string[]) => reorderItemsMut.mutateAsync(itemIds),
    // Event mutations
    addEvent: (itemId: string, input: Record<string, unknown>) =>
      addEventMut.mutateAsync({ itemId, input }),
    updateEvent: (eventId: string, input: Record<string, unknown>) =>
      updateEventMut.mutateAsync({ eventId, input }),
    deleteEvent: (eventId: string) => deleteEventMut.mutateAsync(eventId),
    // Day duplication
    duplicateDay: (itemId: string) => duplicateDayMut.mutateAsync(itemId),
    // Event reorder / move / copy
    reorderEvents: (itemId: string, eventIds: string[]) =>
      reorderEventsMut.mutateAsync({ itemId, eventIds }),
    moveEvent: (eventId: string, targetItemId: string) =>
      moveEventMut.mutateAsync({ eventId, targetItemId }),
    duplicateEvent: (eventId: string, targetItemId: string) =>
      duplicateEventMut.mutateAsync({ eventId, targetItemId }),
    // Attachments
    addItemAttachment: (itemId: string, fileId: string, label?: string) =>
      addItemAttachmentMut.mutateAsync({ itemId, fileId, label }),
    addEventAttachment: (eventId: string, fileId: string, label?: string) =>
      addEventAttachmentMut.mutateAsync({ eventId, fileId, label }),
    removeAttachment: (attachmentId: string, documentId: string) =>
      removeAttachmentMut.mutateAsync({ attachmentId, documentId }),
    // Import
    importTrip: (data: Record<string, unknown>) => importTripMut.mutateAsync(data),
  };
}
